import type { Address } from './valueObjects/address';
import type { ChildId } from './valueObjects/childId';
import type { Email } from './valueObjects/email';
import type { FacilityId } from './valueObjects/facilityId';
import type { FacilityName } from './valueObjects/facilityName';
import type { ParentId } from './valueObjects/parentId';
import type { PostalCode } from './valueObjects/postalCode';
import type { Tel } from './valueObjects/tel';
import type { FacilityJsonPub, FacilityJsonPvt } from '@/types/domain/childApply/entities/facilityEntity';

import {
  EntityPub,
} from '@/domain/entityPub';
import { DeleteType } from '@/domain/entityRepositoryBase';
import {
  EntityName,
} from '@/infra/appsync';

/**
 * 施設プロパティ非公開
 */
export type FacilityPropsPvt = {
  /** 施設ID */
  facilityId: FacilityId,
  /** 施設名 */
  facilityName: FacilityName,
  /** 親ID */
  parentId: ParentId,
  /** 子ID */
  childId: ChildId,
  /** 郵便番号 */
  postalCode: PostalCode,
  /** 住所 */
  address: Address,
  /** メール */
  email: Email,
  /** 電話 */
  tel: Tel,
}

/**
 * 施設プロパティ公開
 */
export type FacilityPropsPub = Record<string, never>

/**
 * 施設Entity
 */
export class FacilityEntity extends EntityPub<
  FacilityPropsPvt,
  FacilityJsonPvt,
  FacilityPropsPub,
  FacilityJsonPub
> {
  get deleteType(): DeleteType {
    return DeleteType.ParentChildId;
  }

  get deleteKey(): string {
    return this.parentChildId;
  }

  toJsonData(): FacilityJsonPvt {
    return {
      facilityId: this.facilityId.value,
      facilityName: this.facilityName.value,
      parentId: this.parentId.value,
      childId: this.childId.value,
      postalCode: this.postalCode.value,
      address: this.address.value,
      email: this.email.value,
      tel: this.tel.value,
    };
  }

  toJsonPubData(): FacilityJsonPub {
    return {};
  }

  /** ラベル名 */
  static get labelName() {
    return '施設';
  }

  /** 親子ID */
  get parentChildId() {
    return `${this.parentId.value}_${this.childId.value}`;
  }

  /**
   * Entity名
   */
  get entityName(): EntityName {
    const name: EntityName = EntityName.WagakoAppChildApplyFacilityEntity;
    return name;
  }

  /**
   * Entityキー
   */
  get entityKey(): string {
    return `${this.facilityId.value}#${this.parentId.value}_${this.childId.value}`;
  }

  /**
   * 施設ID
   */
  get facilityId(): FacilityId {
    return this._value.facilityId;
  }

  /**
   * 施設ID
   */
  set facilityId(facilityId: FacilityId) {
    this._value.facilityId = facilityId;
  }

  /**
   * 施設名
   */
  get facilityName(): FacilityName {
    return this._value.facilityName;
  }

  /**
   * 施設名
   */
  set facilityName(facilityName: FacilityName) {
    this._value.facilityName = facilityName;
  }

  /**
   * 親ID
   */
  get parentId(): ParentId {
    return this._value.parentId;
  }

  /**
   * 親ID
   */
  set parentId(parentId: ParentId) {
    this._value.parentId = parentId;
  }

  /**
   * 子ID
   */
  get childId(): ChildId {
    return this._value.childId;
  }

  /**
   * 子ID
   */
  set childId(childId: ChildId) {
    this._value.childId = childId;
  }

  /**
   * メール
   */
  get email(): Email {
    return this._value.email;
  }

  /**
   * メール
   */
  set email(email: Email) {
    this._value.email = email;
  }

  /**
   * 郵便番号
   */
  get postalCode(): PostalCode {
    return this._value.postalCode;
  }

  /**
   * 郵便番号
   */
  set postalCode(postalCode: PostalCode) {
    this._value.postalCode = postalCode;
  }

  /**
   * 住所
   */
  get address(): Address {
    return this._value.address;
  }

  /**
   * 住所
   */
  set address(address: Address) {
    this._value.address = address;
  }

  /**
   * 電話
   */
  get tel(): Tel {
    return this._value.tel;
  }

  /**
   * 電話
   */
  set tel(tel: Tel) {
    this._value.tel = tel;
  }
}
